import angular, { IRequestConfig } from "angular";
import { PatchObject, PostObject, Record } from "./crud";
import {
  IApiResponse,
  ICrudRecordResponse,
  IHasResponseStatus,
  IQuery,
  IQueryResponse,
} from "./query";

export const vsName = "recordServerUtility";

export class ServerUtility {
  static $inject = ["$http"];

  constructor(private readonly $http: angular.IHttpService) {}

  post<T>({
    url,
    postObject,
  }: {
    url: string;
    postObject: PostObject;
  }): angular.IPromise<T> {
    const request: IRequestConfig = {
      method: "POST",
      url,
      data: postObject,
    };

    return this.sendApiRequest<T>(request);
  }

  postWithCrudRecordResponse<T extends Record>({
    url,
    postObject,
  }: {
    url: string;
    postObject: PostObject;
  }) {
    return this.post<ICrudRecordResponse<T>>({ url, postObject });
  }

  delete<T extends Record>({
    url,
    id,
  }: {
    url: string;
    id: number;
  }): angular.IPromise<ICrudRecordResponse<T>> {
    const request: IRequestConfig = {
      method: "DELETE",
      url: `${url}/${id}`,
    };

    return this.sendApiRequest(request);
  }

  put<T extends Record>({
    url,
    updateObject,
  }: {
    url: string;
    updateObject: PatchObject;
  }): angular.IPromise<ICrudRecordResponse<T>> {
    const id = updateObject.Id;
    const request: IRequestConfig = {
      method: "PUT",
      url: `${url}/${id}`,
      data: updateObject,
    };

    return this.sendApiRequest(request);
  }

  patch<T extends Record>({
    url,
    patchObject,
  }: {
    url: string;
    patchObject: PatchObject;
  }): angular.IPromise<ICrudRecordResponse<T>> {
    const id = patchObject.Id;

    const patch = { ...patchObject };

    const reset: string[] = (patch.Reset = []);
    Object.entries(patch).forEach((entry) => {
      const [propertyName, propertyValue] = entry;
      if (propertyValue === undefined || propertyValue === null) {
        reset.push(propertyName);
      }
    });

    reset.forEach((property) => {
      delete patch[property];
    });

    const request = {
      method: "PATCH",
      url: `${url}/${id}`,
      data: patch,
    };

    return this.sendApiRequest(request);
  }

  query<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): angular.IPromise<IQueryResponse<T>> {
    const request: angular.IRequestConfig = {
      method: "GET",
      url,
      params: query,
    };

    return this.$http(request).then((result: unknown) => {
      const response = <IQueryResponse<T>>result;
      return response;
    });
  }

  queryWrapped<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): angular.IPromise<IQueryResponse<T>> {
    const request: angular.IRequestConfig = {
      method: "GET",
      url,
      params: query,
    };

    return this.sendApiRequest<T>(request).then((result) =>
      this.unwrapApiResponse(result)
    );
  }

  querySingle<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): angular.IPromise<T | undefined> {
    query.Take = 1;
    return this.query<T>({ url, query }).then((r) =>
      r.Results.find((r) => true)
    );
  }

  querySingleWrapped<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): angular.IPromise<T | undefined> {
    query.Take = 1;
    return this.queryWrapped<T>({ url, query }).then((r) =>
      r.Results.find((r) => true)
    );
  }

  async fetchAllWrapped<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): Promise<T[]> {
    query.Skip = 0;
    query.Take = 1000;
    let total = Number.MAX_SAFE_INTEGER;
    const allResults: T[] = [];

    do {
      const queryResult = await this.queryWrapped<T>({ url, query });
      allResults.push(...queryResult.Results);
      total = queryResult.Total;
      query.Skip = allResults.length;
    } while (allResults.length < total);

    return allResults;
  }

  async fetchAll<T>({
    url,
    query,
  }: {
    url: string;
    query: IQuery;
  }): Promise<T[]> {
    query.Skip = 0;
    query.Take = 1000;
    let total = Number.MAX_SAFE_INTEGER;
    const allResults: T[] = [];

    do {
      const queryResult = await this.query<T>({ url, query });
      allResults.push(...queryResult.Results);
      total = queryResult.Total;
      query.Skip = allResults.length;
    } while (allResults.length < total);

    return allResults;
  }

  getByIdWrapped<T>({
    url,
    id,
  }: {
    url: string;
    id: number;
  }): angular.IPromise<T | undefined> {
    const request: IRequestConfig = {
      method: "GET",
      url: `${url}/${id}`,
    };

    return this.sendApiRequest<IApiResponse<T>>(request).then(
      (response) => response.Result
    );
  }

  getById<T>({ url, id }: { url: string; id: number }) {
    const request: IRequestConfig = {
      method: "GET",
      url: `${url}/${id}`,
    };

    return this.sendApiRequest<T>(request);
  }

  lookupQuery<T>({
    url,
    search,
  }: {
    url: string;
    search: string;
  }): angular.IPromise<IQueryResponse<T>> {
    const request = {
      method: "GET",
      url,
      params: this.getParams(search),
    };

    return this.$http(request).then((result: unknown) => {
      var queryResponse = <IQueryResponse<T>>result;
      return queryResponse;
    });
  }

  lookupQueryWrapped<T>({
    url,
    search,
  }: {
    url: string;
    search: string;
  }): angular.IPromise<IQueryResponse<T>> {
    return this.queryWrapped<T>({
      url,
      query: {
        NameContains: search,
        OrderBy: "Name",
        Take: 10,
      },
    });
  }

  alreadyExists({
    url,
    query,
    excludeId,
  }: {
    url: string;
    query: IQuery;
    excludeId?: number;
  }): angular.IPromise<boolean> {
    query.Take = 2;

    return this.query({ url, query }).then((response) => {
      const recordResponse = <IQueryResponse<Record>>response;
      return (
        recordResponse.Results.find(
          (r) => excludeId === undefined || r.Id !== excludeId
        ) !== undefined
      );
    });
  }

  alreadyExistsWrapped({
    url,
    query,
    excludeId,
  }: {
    url: string;
    query: IQuery;
    excludeId?: number;
  }): angular.IPromise<boolean> {
    query.Take = 2;

    return this.queryWrapped({ url, query }).then((response) => {
      const recordResponse = <IQueryResponse<Record>>response;
      return (
        recordResponse.Results.find(
          (r) => excludeId === undefined || r.Id !== excludeId
        ) !== undefined
      );
    });
  }

  private getParams(search: string) {
    return {
      NameContains: search,
      OrderBy: "Name",
      Take: 10,
    };
  }

  private unwrapApiResponse<T>(apiResponse: unknown): T {
    const t = <IApiResponse<T>>apiResponse;
    return t?.Result;
  }

  sendApiRequest<TResponse>(
    request: IRequestConfig
  ): angular.IPromise<TResponse> {
    return this.$http(request).then((result: unknown) => {
      const apiResponse = <TResponse>result;
      return this.handleResponse<TResponse>(apiResponse);
    });
  }

  private handleResponse<TResponse>(apiResponse: TResponse): TResponse {
    const hasResponseStatus = <IHasResponseStatus>apiResponse;
    if (hasResponseStatus?.ResponseStatus?.ErrorCode ?? "") {
      throw new ApiError(hasResponseStatus);
    }
    return apiResponse;
  }
}

export class ApiError extends Error {
  constructor(readonly apiResponse: IHasResponseStatus) {
    super(apiResponse?.ResponseStatus?.Message);
  }
}
